import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_PATH, SITE_URL } from './seoPages';
import { SeoHead, FaqSection, RelatedLinks, SignupCta } from './SeoShared';
import { loadDirectory, findHsnSuggestions } from '../../utils/hsnDirectory';
import '../products/EffyOrder.css';
import './SeoPages.css';

const MAX_RESULTS = 20;
const TYPING_DELAY_MS = 250;

const FAQS = [
  { question: 'What is an HSN code?', answer: 'HSN stands for Harmonised System of Nomenclature. It is a numeric code that identifies a product, and GST invoices carry it so that the right tax rate can be applied to each item.' },
  { question: 'How do I find the HSN code for my product?', answer: 'Type the product name or the first digits of a code into the finder above. It lists matching codes with their descriptions and the GST rate saved in the directory.' },
  { question: 'Are the GST rates shown here final?', answer: 'No. They come from a directory file that is updated from time to time, and rates can change. Confirm the HSN code and rate for your product with your accountant or the GST portal before you use them on an invoice.' },
  { question: 'Can Effy Order store HSN codes and rates for my items?', answer: 'Yes. You save each HSN code with its GST rate in your tax profile, attach it to your items and every invoice picks the rate automatically. HSN suggestions are also shown while you fill in the tax profile.' },
  { question: 'Why are some codes listed with more than one rate?', answer: 'Some codes appear with different descriptions and rates in the directory. Read the description carefully and check with your accountant which one applies to your product.' },
];

const FINDER_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'HSN Code Finder',
  url: `${SITE_URL}/tools/hsn-code-finder`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
};

function describeRate(rate) {
  if (rate === null || rate === undefined) {
    return 'Rate not listed';
  }
  return `${rate}% GST`;
}

// Free tool: search the HSN directory by product name or code. The directory file
// (about 1.4 MB) is only downloaded after the visitor starts typing.
export default function HsnFinderPage() {
  const [query, setQuery] = useState('');
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Wait until the visitor pauses typing before searching.
  useEffect(() => {
    const timerId = setTimeout(() => {
      setSearchText(query.trim());
    }, TYPING_DELAY_MS);
    return () => clearTimeout(timerId);
  }, [query]);

  useEffect(() => {
    if (searchText === '') {
      setResults([]);
      setStatus('idle');
      return undefined;
    }
    let ignore = false;
    setStatus('loading');
    loadDirectory().then((directory) => {
      if (ignore) {
        return;
      }
      if (directory.length === 0) {
        setResults([]);
        setStatus('error');
        return;
      }
      setResults(findHsnSuggestions(directory, searchText, MAX_RESULTS));
      setStatus('done');
    });
    return () => {
      ignore = true;
    };
  }, [searchText]);

  let message = null;
  if (status === 'loading') {
    message = 'Searching the HSN directory...';
  }
  if (status === 'error') {
    message = 'The HSN directory could not be loaded. Please check your connection and try again.';
  }
  if (status === 'done' && results.length === 0) {
    message = 'No matching HSN code found. Try a shorter word or the first digits of the code.';
  }

  return (
    <div className="oi-page-v2">
      <SeoHead
        path="/tools/hsn-code-finder"
        title="HSN Code Finder: Search Codes and GST Rates | EffyVenture"
        description="Free HSN code finder for India. Search by product name or code to see the HSN description and the GST rate listed for it. Confirm rates with your accountant."
        breadcrumbName="HSN Code Finder"
        faqs={FAQS}
        extraSchema={FINDER_SCHEMA}
      />

      <section className="oi-hero-v2 seo-hero--center">
        <div className="seo-hero__center">
          <div className="oi-badge-v2">Free tool</div>
          <h1 className="oi-hero-v2__title">
            HSN Code Finder <span className="text-gradient">— search codes and GST rates</span>
          </h1>
          <p className="oi-hero-v2__subtitle">
            Type a product name such as mobile phone, or the first digits of an HSN code, to see matching codes and the GST rate listed for each.
          </p>
        </div>
      </section>

      <section className="seo-calc">
        <div className="seo-hsn__card">
          <label className="seo-calc__field">
            <span className="seo-calc__label">Product name or HSN code</span>
            <input
              className="seo-calc__input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="For example: rice, 8517 or mobile phone"
              autoComplete="off"
            />
          </label>

          {message && <p className="seo-hsn__message" aria-live="polite">{message}</p>}

          {results.length > 0 && (
            <ul className="seo-hsn__list" aria-live="polite">
              {results.map((row) => (
                <li key={`${row[0]}-${row[1]}-${row[2]}`} className="seo-hsn__item">
                  <span className="seo-hsn__code">{row[0]}</span>
                  <span className="seo-hsn__name">{row[1]}</span>
                  <span className="seo-hsn__rate">{describeRate(row[2])}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="seo-calc__note">
          Rates come from a directory file and can change, so confirm them with your accountant. Billing every day?{' '}
          <Link to={SIGNUP_PATH}>Effy Order</Link> keeps each item’s HSN code and GST rate and applies the right tax on every invoice.
        </p>
      </section>

      <FaqSection faqs={FAQS} />
      <RelatedLinks paths={['/products/effy-order/gst-billing-software', '/tools/gst-calculator', '/gst-invoice-format-india']} />
      <SignupCta />
    </div>
  );
}
